/**
 * Cross-lab strain families — the same yeast, banked by different labs.
 *
 * A brewer standing in a shop that only stocks one lab is asking a question
 * the per-strain record cannot answer: *what is the WLP number for 1056?*
 * Every lab sells its own cut of the handful of strains that matter, under its
 * own code, and nothing in the catalog related them.
 *
 * ## Why a record rather than a slug on the yeast
 *
 * Hops solve the same shape with `hopCultivarSchema`: same-genotype lots share
 * a slug and {@link resolveCultivarSiblings} returns everyone carrying it. That
 * slug is itself a catalog id — the landrace — so the family gets its name and
 * its provenance free from a real entry.
 *
 * **Yeast has no landrace.** No lab's Chico is *the* Chico; the strain's origin
 * is Sierra Nevada's house culture, which is not a product in this or any other
 * catalog. Naming one member canonical would invent a hierarchy the industry
 * does not have, and there would still be nowhere to put the sentence below.
 *
 * ## What the catalog is and is not claiming
 *
 * Almost none of this is lab-published. White Labs has never written that
 * WLP001 is US-05; the equivalences are held by brewers, supported by
 * fermentation behaviour and — for a few families — by sequencing done outside
 * the labs. So the panel hedges once, under the heading, for every family
 * alike, and this schema carries **no per-membership confidence field**: a
 * three-state flag for the handful of well-evidenced cases would buy nothing
 * the sentence does not, and would invite authors to grade folklore.
 *
 * Where a family is unusually well evidenced, its {@link YeastFamily.description}
 * says so in prose. Where it is thin, it does not ship.
 */

import { z } from 'zod';
import { descriptionSchema, slugSchema } from './common';

/**
 * One strain, and every catalog entry that is a cut of it.
 *
 * `members` are yeast ids and every one of them resolves — `yeast-families.test.ts`
 * enforces it — so a caller resolving a family never has to reason about a
 * dangling reference.
 */
export const yeastFamilySchema = z.strictObject({
  /** Stable slug for the strain itself, not for any lab's product: `chico`. */
  id: slugSchema,
  /** What brewers call the strain: `Chico`, `Weihenstephan 34/70`. */
  name: z.string().min(2).max(60),
  /**
   * What the strain is, where it came from, and how solid the claim is.
   *
   * The place for a family's own nuance — a sequencing result, a lab that has
   * confirmed its cut, a member that behaves noticeably differently. The UI's
   * standing hedge covers the general case; this covers the specific one.
   */
  description: descriptionSchema,
  /**
   * Yeast ids belonging to this family, at least two.
   *
   * Two, because a family of one is not an equivalence — it is a yeast, and the
   * panel already shows it.
   */
  members: z.array(slugSchema).min(2),
});

/** @see yeastFamilySchema */
export type YeastFamily = z.infer<typeof yeastFamilySchema>;
